// @greenlight-spec: 10-security-compliance.md#compliance-evidence-not-compliance-bundles
// @greenlight-spec: 16-api-conventions.md
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorResponses } from './common.js';
const c = initContract();
/**
 * What a `gate` row's checks do **in this org**, which is not a property of the
 * mapping. Code-finding checks ship at `warn` on purpose (an org that cannot
 * land its first deploy stops using Greenlight), so a static "enforced" claim
 * would tell most installs their merges are blocked when they are not.
 *
 * `enforced` — every rule behind the control is `block` here, so a finding
 * holds the merge. `reported` — at least one is `warn` or off, so findings are
 * recorded on the run without gating. `n/a` — an `evidence` row, where nothing
 * gates by design.
 */
export const Soc2CoverageStateSchema = z.enum(['enforced', 'reported', 'n/a']);
export const Soc2ControlRowSchema = z.object({
    control: z.string(),
    family: z.enum(['CC6', 'CC7', 'CC8']),
    title: z.string(),
    mechanism: z.string(),
    kind: z.enum(['gate', 'evidence']),
    policy_ids: z.array(z.string()),
    /** Resolved against this org's tuning, not the mapping's defaults. */
    coverage: Soc2CoverageStateSchema,
    evidence_href: z.string(),
});
/**
 * Where a deploy's SBOM actually is, stated rather than linked.
 *
 * The SBOM is produced in CI and kept as a build artifact with a fixed
 * retention; Greenlight verifies it at gate time and does not copy it. So for a
 * deploy older than that window the SBOM is gone, and a view that rendered a
 * download button anyway would be handing an auditor a dead link. `available`
 * says which case this is, and `expires_at` says when the answer changes.
 */
export const SbomPointerSchema = z.object({
    available: z.boolean(),
    /**
     * `greenlight` — a durable copy Greenlight took at gate time, which does not
     * expire. `ci_artifact` — no durable copy, so the only SBOM is the build
     * artifact and it lasts as long as CI keeps it.
     */
    source: z.enum(['greenlight', 'ci_artifact']),
    retention_days: z.number().int().positive().nullable(),
    expires_at: z.string().datetime({ offset: true }).nullable(),
    scm_run_id: z.string().nullable(),
});
export const Soc2AppEvidenceSchema = z.object({
    app_id: z.string().uuid(),
    app_name: z.string(),
    app_slug: z.string(),
    run_id: z.string().uuid().nullable(),
    commit_sha: z.string().nullable(),
    run_status: z.string().nullable(),
    completed_at: z.string().datetime({ offset: true }).nullable(),
    /**
     * Whether the recorded gate decision allowed the deploy. Null when the run
     * predates the gate persisting its decision — absence of a decision is not
     * the same as a decision to allow, and the view must not read it as one.
     */
    gate_allowed: z.boolean().nullable(),
    checks: z.array(z.object({ name: z.string(), status: z.string() })),
    /**
     * The pull request whose merge authorized this deploy. The deploy itself is
     * executed by the platform, so the human authorization lives in the SCM
     * record, not in an actor column here.
     */
    pull_request_number: z.number().int().nullable(),
    sbom: SbomPointerSchema,
});
export const Soc2EvidenceSchema = z.object({
    framework: z.literal('SOC 2'),
    generated_at: z.string().datetime({ offset: true }),
    /** Carried in the payload so an export cannot be read without it. */
    scope_note: z.string(),
    controls: z.array(Soc2ControlRowSchema),
    apps: z.array(Soc2AppEvidenceSchema),
});
export const SbomDownloadSchema = z.object({
    /** Short-lived, read-only URL for the stored document. */
    url: z.string().url(),
    commit_sha: z.string(),
});
export const HipaaControlRowSchema = z.object({
    control: z.string(),
    safeguard: z.enum(['administrative', 'technical']),
    title: z.string(),
    mechanism: z.string(),
    kind: z.enum(['gate', 'evidence']),
    policy_ids: z.array(z.string()),
    /** Resolved against this org's tuning, not the mapping's defaults. */
    coverage: Soc2CoverageStateSchema,
    evidence_href: z.string(),
});
/**
 * The agreement standing behind one PHI-tagged integration.
 *
 * `state` is computed, not stored: an agreement lapses on its own end date, so a view
 * reading a stored flag could show an auditor "on file" for paperwork that expired last
 * quarter. `expired` and `revoked` are reported distinctly from `missing` because they
 * are different findings — one is a renewal, the other was a decision.
 */
export const BaaRecordSchema = z.object({
    state: z.enum(['active', 'expired', 'revoked', 'missing']),
    counterparty: z.string().nullable(),
    reference: z.string().nullable(),
    signed_at: z.string().datetime({ offset: true }).nullable(),
    expires_at: z.string().datetime({ offset: true }).nullable(),
});
export const PhiIntegrationEvidenceSchema = z.object({
    integration_id: z.string().uuid(),
    name: z.string(),
    display_name: z.string(),
    /** Every compliance tag on the integration, not just the PHI one. */
    compliance_tags: z.array(z.string()),
    baa: BaaRecordSchema,
    /**
     * Whether the gate would refuse a grant on this integration as things stand —
     * the agreement is not active *and* `hipaa.baa_required` blocks in this org.
     * The check ships at `warn`, where the finding is recorded on the run without
     * holding the deploy, so reading the BAA state alone would tell an auditor
     * deploys are being refused when they are not.
     */
    grant_blocked: z.boolean(),
});
export const HipaaEvidenceSchema = z.object({
    framework: z.literal('HIPAA Security Rule'),
    generated_at: z.string().datetime({ offset: true }),
    /** Carried in the payload so an export cannot be read without it. */
    scope_note: z.string(),
    controls: z.array(HipaaControlRowSchema),
    /**
     * The integrations IT put in HIPAA scope. Empty means no integration is tagged for
     * PHI, which is the state every org starts in — the view renders nothing rather than
     * advertising a framework the org does not use.
     */
    phi_integrations: z.array(PhiIntegrationEvidenceSchema),
});
export const GdprControlRowSchema = z.object({
    control: z.string(),
    chapter: z.enum(['transfers', 'security', 'accountability']),
    title: z.string(),
    mechanism: z.string(),
    kind: z.enum(['gate', 'evidence']),
    policy_ids: z.array(z.string()),
    coverage: Soc2CoverageStateSchema,
    evidence_href: z.string(),
});
export const GdprIntegrationEvidenceSchema = z.object({
    integration_id: z.string().uuid(),
    name: z.string(),
    display_name: z.string(),
    /** Recorded residency metadata; empty means none on file. */
    region: z.string(),
    /**
     * A non-empty trimmed region is on file. The gate checks that IT recorded one,
     * not that anyone approved it: Article 44 approval is a legal determination
     * about transfer basis, and no string match can stand in for it.
     */
    residency_recorded: z.boolean(),
    /**
     * Whether the gate would refuse a grant on this integration as things stand —
     * residency is not approved *and* `gdpr.residency_required` blocks in this org.
     * The check ships at `warn`, so reading the region alone would tell an auditor
     * deploys are being refused when they are not.
     */
    grant_blocked: z.boolean(),
});
export const GdprEvidenceSchema = z.object({
    framework: z.literal('GDPR'),
    generated_at: z.string().datetime({ offset: true }),
    scope_note: z.string(),
    /**
     * Whether this org is designated as processing EU-resident data. False is the
     * dormant state every org starts in — the view renders nothing rather than
     * advertising a framework the org does not use.
     */
    eu_designated: z.boolean(),
    controls: z.array(GdprControlRowSchema),
    integrations: z.array(GdprIntegrationEvidenceSchema),
});
export const complianceContract = c.router({
    soc2: {
        method: 'GET',
        path: '/api/compliance/soc2',
        responses: {
            200: Soc2EvidenceSchema,
            ...errorResponses,
        },
        summary: "SOC 2 control coverage mapped to shipped Greenlight mechanisms, with each app's latest deploy evidence. Read-only, IT-scoped. Coverage and evidence — not an attestation of compliance.",
    },
    sbom: {
        method: 'GET',
        path: '/api/compliance/runs/:runId/sbom',
        pathParams: z.object({ runId: z.string().uuid() }),
        responses: {
            200: SbomDownloadSchema,
            ...errorResponses,
        },
        summary: "Short-lived download URL for a deploy's retained SBOM. 404 when no durable copy was taken — retention is best-effort and never blocks a deploy.",
    },
    hipaa: {
        method: 'GET',
        path: '/api/compliance/hipaa',
        responses: {
            200: HipaaEvidenceSchema,
            ...errorResponses,
        },
        summary: 'HIPAA Security Rule safeguard coverage, with the BAA standing behind each PHI-tagged integration. Read-only, IT-scoped. Coverage and evidence — not an attestation of compliance.',
    },
    gdpr: {
        method: 'GET',
        path: '/api/compliance/gdpr',
        responses: {
            200: GdprEvidenceSchema,
            ...errorResponses,
        },
        summary: 'GDPR article coverage, with the residency standing behind each integration in an EU-designated org. Read-only, IT-scoped. Coverage and evidence — not an attestation of compliance.',
    },
});
//# sourceMappingURL=compliance.js.map